// Reports when a signed-in member's browser is running a build the server has replaced.
//
// Written for VA-249 as a temporary diagnostic; kept permanently after VA-270, where its
// single row — running index-DJiHORFd.js while the server served index-Crzhs_BP.js — was
// what identified the daily white screen as a service worker stuck on an old build. Three
// fixes had already shipped against the wrong cause.
//
// It has one blind spot worth knowing: it runs from DashboardLayout, so it cannot fire when
// the screen is blank and nothing mounts. blankScreenWatch covers that case and reports
// through the same route.
//
// Bridget keeps reporting members who "still see the old options". Every time, the answer
// has been that the member is running a build from before the fix, but we only ever find
// that out by opening their account and looking. This reports it instead: when a signed-in
// member is running JavaScript that is not what the server is currently serving, it says so,
// and says who.
//
// HOW IT DETECTS STALENESS
// Vite fingerprints the entry bundle, so the running page knows which assets/index-<hash>.js
// it loaded. Asking the server for index.html with cache:"no-store" gives the hash it serves
// right now. Two different hashes means this browser is running code we replaced. That is a
// direct observation rather than a guess from a version number, and it survives the service
// worker, which is the thing causing the staleness in the first place.
//
// WHAT IT COSTS
// One extra request, once per session, five seconds after load, only for signed-in members,
// and it reports at most once per stale build per session. It never blocks anything and it
// never throws: every failure path is swallowed, because a diagnostic that breaks the app it
// is diagnosing is worse than the bug.
import { supabase } from "@/integrations/supabase/client";
import { debug } from "@/lib/debug";

const REPORTED_KEY = "va249-stale-reported";
const BUILD_VERSION = (import.meta as { env?: Record<string, string> }).env?.VITE_BUILD_TIMESTAMP || "dev";

/** The fingerprinted entry bundle this page is actually running. */
function runningEntryAsset(): string | null {
  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"));
  const entry = scripts.map((s) => s.src).find((src) => /\/assets\/index-[A-Za-z0-9_-]+\.js/.test(src));
  return entry ? entry.split("/").pop()! : null;
}

/** The entry bundle the server is serving right now, bypassing every cache. */
async function servedEntryAsset(): Promise<string | null> {
  const res = await fetch(`/?va249=${Date.now()}`, { cache: "no-store", credentials: "same-origin" });
  if (!res.ok) return null;
  const html = await res.text();
  return html.match(/assets\/index-[A-Za-z0-9_-]+\.js/)?.[0]?.split("/").pop() ?? null;
}

async function describeEnvironment() {
  let workers = -1;
  let cacheNames: string[] = [];
  try { workers = (await navigator.serviceWorker?.getRegistrations?.())?.length ?? -1; } catch { /* ignore */ }
  try { cacheNames = "caches" in window ? await caches.keys() : []; } catch { /* ignore */ }
  return {
    workers,
    caches: cacheNames,
    standalone: window.matchMedia?.("(display-mode: standalone)")?.matches ?? false,
    lastActive: localStorage.getItem("vb-last-active-timestamp"),
    storedVersion: localStorage.getItem("vb-app-version"),
  };
}

export async function checkForStaleClient(): Promise<void> {
  try {
    if (BUILD_VERSION === "dev") return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return; // Nothing useful to report without knowing who.

    const running = runningEntryAsset();
    const served = await servedEntryAsset();
    if (!running || !served || running === served) return;

    // One report per stale pairing per session, so a member sitting on an old build for an
    // afternoon produces one row rather than one per navigation.
    const marker = `${running}->${served}`;
    if (sessionStorage.getItem(REPORTED_KEY) === marker) return;
    sessionStorage.setItem(REPORTED_KEY, marker);

    const env = await describeEnvironment();
    const staleFor = env.lastActive ? Math.round((Date.now() - Number(env.lastActive)) / 60000) : null;

    const message = [
      `STALE CLIENT — this browser is running an old build.`,
      `email: ${session.user.email ?? "unknown"}`,
      `running: ${running}`,
      `served:  ${served}`,
      `buildStamp: ${BUILD_VERSION}  storedVersion: ${env.storedVersion ?? "none"}`,
      `serviceWorkers: ${env.workers}  caches: ${env.caches.join(", ") || "none"}`,
      `installedApp: ${env.standalone}  minutesSinceLastActive: ${staleFor ?? "unknown"}`,
    ].join("\n");

    debug.log("[VA-249]", message);

    // userId is taken from the auth token server-side, so the row is always attributable.
    await supabase.functions.invoke("log-error", {
      body: {
        category: "browser_compat",
        action: "stale-client",
        message,
        status: null,
        userAgent: navigator.userAgent,
        url: window.location.href,
      },
    });
  } catch (e) {
    debug.log("[VA-249] stale check failed (ignored):", e);
  }
}
